// src/ml/bakeProjectedUvAtlas.js

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

/**
 * Phase 2A UV baker.
 *
 * Takes:
 * - normal product image / extracted front PNG
 * - garment GLB with UVs
 *
 * Creates:
 * - a UV atlas by projecting the product image onto the GLB,
 *   then writing that projected colour into UV space.
 *
 * This is still prototype-level:
 * - front image only
 * - no true front/back split yet
 * - no seam blending yet
 * - unknown UV areas are filled with sampled fabric colour
 */

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Missing image src"));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));

    img.src = src;
  });
}

function loadGLB(url) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();

    loader.load(
      url,
      (gltf) => resolve(gltf),
      undefined,
      (err) => reject(err)
    );
  });
}

function findFirstMesh(root) {
  let found = null;

  root.traverse?.((obj) => {
    if (found) return;
    if ((obj.isMesh || obj.isSkinnedMesh) && obj.geometry) {
      found = obj;
    }
  });

  return found;
}

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

function clamp255(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function rgbToCss({ r, g, b }) {
  return `rgb(${clamp255(r)}, ${clamp255(g)}, ${clamp255(b)})`;
}

function sampleVisiblePixels(ctx, width, height, options = {}) {
  const {
    alphaThreshold = 24,
    maxSamples = 9000,
    ignoreNearWhite = true,
    whiteThreshold = 238,
  } = options;

  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  const pixels = [];
  const totalPixels = width * height;
  const step = Math.max(1, Math.floor(totalPixels / maxSamples));

  for (let i = 0; i < totalPixels; i += step) {
    const idx = i * 4;

    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    const a = data[idx + 3];

    if (a < alphaThreshold) continue;

    if (
      ignoreNearWhite &&
      r > whiteThreshold &&
      g > whiteThreshold &&
      b > whiteThreshold
    ) {
      continue;
    }

    pixels.push({ r, g, b, a });
  }

  return pixels;
}

function averageColor(pixels, fallback = { r: 120, g: 130, b: 145 }) {
  if (!pixels?.length) return fallback;

  let r = 0;
  let g = 0;
  let b = 0;
  let total = 0;

  for (const p of pixels) {
    const w = Math.max(1, p.a / 255);
    r += p.r * w;
    g += p.g * w;
    b += p.b * w;
    total += w;
  }

  if (!total) return fallback;

  return {
    r: r / total,
    g: g / total,
    b: b / total,
  };
}

function sampleImageAt(imageData, width, height, u, v) {
  const x = Math.floor(clamp01(u) * (width - 1));
  const y = Math.floor(clamp01(v) * (height - 1));

  const idx = (y * width + x) * 4;
  const data = imageData.data;

  return {
    r: data[idx],
    g: data[idx + 1],
    b: data[idx + 2],
    a: data[idx + 3],
  };
}

function barycentric(px, py, ax, ay, bx, by, cx, cy) {
  const v0x = bx - ax;
  const v0y = by - ay;
  const v1x = cx - ax;
  const v1y = cy - ay;
  const v2x = px - ax;
  const v2y = py - ay;

  const den = v0x * v1y - v1x * v0y;
  if (Math.abs(den) < 1e-8) return null;

  const v = (v2x * v1y - v1x * v2y) / den;
  const w = (v0x * v2y - v2x * v0y) / den;
  const u = 1 - v - w;

  if (u < -0.001 || v < -0.001 || w < -0.001) return null;

  return { u, v, w };
}

function addSubtleNoise(ctx, size, strength = 3) {
  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const n = (Math.random() - 0.5) * strength;

    data[i] = clamp255(data[i] + n);
    data[i + 1] = clamp255(data[i + 1] + n);
    data[i + 2] = clamp255(data[i + 2] + n);
    data[i + 3] = 255;
  }

  ctx.putImageData(imageData, 0, 0);
}

export async function bakeProjectedUvAtlas({
  imageUrl,
  glbUrl,
  size = 1024,
  fallbackColor = { r: 120, g: 130, b: 145 },
  alphaThreshold = 24,
  debug = false,
} = {}) {
  if (!imageUrl) {
    throw new Error("bakeProjectedUvAtlas requires imageUrl");
  }

  if (!glbUrl) {
    throw new Error("bakeProjectedUvAtlas requires glbUrl");
  }

  const [img, gltf] = await Promise.all([
    loadImage(imageUrl),
    loadGLB(glbUrl),
  ]);

  const mesh = findFirstMesh(gltf.scene);
  if (!mesh?.geometry) {
    throw new Error("No mesh with geometry found in GLB");
  }

  const geometry = mesh.geometry;
  const pos = geometry.attributes.position;
  const uv = geometry.attributes.uv;
  const index = geometry.index;

  if (!pos || !uv) {
    throw new Error("GLB mesh must have position and uv attributes");
  }

  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }

  const bb = geometry.boundingBox;
  const min = bb.min;
  const max = bb.max;

  const width = Math.max(1e-6, max.x - min.x);
  const height = Math.max(1e-6, max.y - min.y);

  // Prepare source image canvas.
  const sourceCanvas = document.createElement("canvas");
  const sourceSize = 1024;

  sourceCanvas.width = sourceSize;
  sourceCanvas.height = sourceSize;

  const sourceCtx = sourceCanvas.getContext("2d", { willReadFrequently: true });
  if (!sourceCtx) {
    throw new Error("Could not create source canvas context");
  }

  sourceCtx.clearRect(0, 0, sourceSize, sourceSize);
  sourceCtx.drawImage(img, 0, 0, sourceSize, sourceSize);

  const sourceImageData = sourceCtx.getImageData(0, 0, sourceSize, sourceSize);

  const pixels = sampleVisiblePixels(sourceCtx, sourceSize, sourceSize, {
    alphaThreshold,
  });

  const sampledColor = averageColor(pixels, fallbackColor);

  // Slight lift so dark garments don't crush to black.
  const baseColor = {
    r: sampledColor.r * 1.14 + 10,
    g: sampledColor.g * 1.14 + 10,
    b: sampledColor.b * 1.14 + 12,
  };

  // Prepare atlas.
  const atlasCanvas = document.createElement("canvas");
  atlasCanvas.width = size;
  atlasCanvas.height = size;

  const atlasCtx = atlasCanvas.getContext("2d", { willReadFrequently: true });
  if (!atlasCtx) {
    throw new Error("Could not create atlas canvas context");
  }

  // Fill unknown UV space with sampled fabric colour.
  atlasCtx.fillStyle = rgbToCss(baseColor);
  atlasCtx.fillRect(0, 0, size, size);

  const atlasImageData = atlasCtx.getImageData(0, 0, size, size);
  const atlasData = atlasImageData.data;

  let paintedPixels = 0;
  let skippedTransparent = 0;

  const triCount = index ? index.count / 3 : pos.count / 3;

  let uvMinX = Infinity;
let uvMinY = Infinity;
let uvMaxX = -Infinity;
let uvMaxY = -Infinity;

for (let i = 0; i < uv.count; i++) {
  const ux = uv.getX(i);
  const uy = uv.getY(i);

  uvMinX = Math.min(uvMinX, ux);
  uvMinY = Math.min(uvMinY, uy);
  uvMaxX = Math.max(uvMaxX, ux);
  uvMaxY = Math.max(uvMaxY, uy);
}

console.log("[UV BAKE] raw UV range", {
  uvMinX,
  uvMaxX,
  uvMinY,
  uvMaxY,
});


  function getVertexIndex(tri, corner) {
    if (index) return index.getX(tri * 3 + corner);
    return tri * 3 + corner;
  }

  for (let tri = 0; tri < triCount; tri++) {
    const ia = getVertexIndex(tri, 0);
    const ib = getVertexIndex(tri, 1);
    const ic = getVertexIndex(tri, 2);


const uvRangeX = Math.max(1e-6, uvMaxX - uvMinX);
const uvRangeY = Math.max(1e-6, uvMaxY - uvMinY);

const uvaX = (uv.getX(ia) - uvMinX) / uvRangeX;
const uvaY = (uv.getY(ia) - uvMinY) / uvRangeY;

const uvbX = (uv.getX(ib) - uvMinX) / uvRangeX;
const uvbY = (uv.getY(ib) - uvMinY) / uvRangeY;

const uvcX = (uv.getX(ic) - uvMinX) / uvRangeX;
const uvcY = (uv.getY(ic) - uvMinY) / uvRangeY;

const ax = uvaX * size;
const ay = (1 - uvaY) * size;

const bx = uvbX * size;
const by = (1 - uvbY) * size;

const cx = uvcX * size;
const cy = (1 - uvcY) * size;

    const minX = Math.max(0, Math.floor(Math.min(ax, bx, cx)));
    const maxX = Math.min(size - 1, Math.ceil(Math.max(ax, bx, cx)));
    const minY = Math.max(0, Math.floor(Math.min(ay, by, cy)));
    const maxY = Math.min(size - 1, Math.ceil(Math.max(ay, by, cy)));

    if (maxX < minX || maxY < minY) continue;

    const pa = new THREE.Vector3(pos.getX(ia), pos.getY(ia), pos.getZ(ia));
    const pb = new THREE.Vector3(pos.getX(ib), pos.getY(ib), pos.getZ(ib));
    const pc = new THREE.Vector3(pos.getX(ic), pos.getY(ic), pos.getZ(ic));

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const bc = barycentric(
          x + 0.5,
          y + 0.5,
          ax,
          ay,
          bx,
          by,
          cx,
          cy
        );

        if (!bc) continue;

        const localX = pa.x * bc.u + pb.x * bc.v + pc.x * bc.w;
        const localY = pa.y * bc.u + pb.y * bc.v + pc.y * bc.w;
// Match GarmentMesh bottom projection rules more closely.
// These should line up with GARMENT_RULES.bottom in GarmentMesh.jsx.
const wrapX = 0.08;
const widthBoost = 0.82;
const yShift = 0.1;
const heightBoost = 0.85;

let projectedU = (localX - min.x) / width;
let projectedV = (localY - min.y) / height;

// Same order as sampleTex() in GarmentMesh.jsx
projectedV = projectedV + yShift;
projectedV = 1.0 - projectedV;

projectedU = (projectedU - 0.5) * (1.0 - wrapX) * widthBoost + 0.5;
projectedV = (projectedV - 0.5) * (1.0 / heightBoost) + 0.5;

        const c = sampleImageAt(
          sourceImageData,
          sourceSize,
          sourceSize,
          projectedU,
          projectedV
        );

        if (c.a < alphaThreshold) {
        skippedTransparent++;
        continue;
        }

        const idx = (y * size + x) * 4;

        // Alpha-aware blend so semi-transparent mask edges don't create harsh blocks.
        const sourceA = Math.max(0, Math.min(1, c.a / 255));

        atlasData[idx] = clamp255(atlasData[idx] * (1 - sourceA) + c.r * sourceA);
        atlasData[idx + 1] = clamp255(atlasData[idx + 1] * (1 - sourceA) + c.g * sourceA);
        atlasData[idx + 2] = clamp255(atlasData[idx + 2] * (1 - sourceA) + c.b * sourceA);
        atlasData[idx + 3] = 255;

        paintedPixels++;
      }
    }
  }

  atlasCtx.putImageData(atlasImageData, 0, 0);

  // Very mild noise only on final atlas, so empty/fill areas don't look totally flat.
  addSubtleNoise(atlasCtx, size, 2);

  if (debug) {
    atlasCtx.save();
    atlasCtx.globalAlpha = 0.35;
    atlasCtx.strokeStyle = "white";
    atlasCtx.lineWidth = 2;

    for (let i = 0; i <= size; i += size / 8) {
      atlasCtx.beginPath();
      atlasCtx.moveTo(i, 0);
      atlasCtx.lineTo(i, size);
      atlasCtx.stroke();

      atlasCtx.beginPath();
      atlasCtx.moveTo(0, i);
      atlasCtx.lineTo(size, i);
      atlasCtx.stroke();
    }

    atlasCtx.restore();
  }

  return {
  dataUrl: atlasCanvas.toDataURL("image/png"),
  baseColor,
  sampleCount: pixels.length,
  paintedPixels,
  skippedTransparent,
  meshName: mesh.name || null,
  vertexCount: pos.count,
  uvCount: uv.count,
  triCount,
  uvRange: {
    minX: uvMinX,
    maxX: uvMaxX,
    minY: uvMinY,
    maxY: uvMaxY,
  },
};
}